import { parseISO } from "date-fns";
import { ReactNode, useCallback, useEffect, useMemo } from "react";
import { StyleProp, StyleSheet, View, ViewStyle } from "react-native";

import {
  FormDatePicker,
  FormError,
  FormProvider,
  FormSelect,
  FormTextInput,
  ResetButton,
  SubmitButton,
  useFormWrapper,
  useWatch,
} from "@luna-restaurant/forms";
import { useTranslation } from "@luna-restaurant/i18n";
import { ThemedText, useCustomTheme } from "@luna-restaurant/ui";

import {
  type ClosingTimeFormValues,
  createClosingTimeFormSchema,
} from "@/components/forms/closingTimeFormSchema";
import { getEarliestClosingTimeRequestStartDate } from "@/utils/closingTimeRequestDateRules";

export type { ClosingTimeFormValues } from "@/components/forms/closingTimeFormSchema";

type ClosingTimeFormProps = {
  schoolClassOptions: { label: string; value: string }[];
  initialValues?: Partial<ClosingTimeFormValues>;
  onSubmit?: (values: ClosingTimeFormValues) => Promise<void>;
  onScopeOrClassIdDirtyChange?: (isDirty: boolean) => void;
  submitButtonText?: string;
  noteComponent?: ReactNode;
  actionsNotice?: ReactNode;
  isSubmitDisabled?: (values: ClosingTimeFormValues) => boolean;
  renderActions?: (handlers: {
    submit: () => void;
    reset: () => void;
    isSubmitting: boolean;
    isSubmitDisabled: boolean;
  }) => ReactNode;
  isDateRangeDisabled?: boolean;
  isScopeDisabled?: boolean;
  isEditorDisabled?: boolean;
  isClosingNoticeDisabled?: boolean;
  fieldLabelMinHeight?: number;
  reserveErrorSpace?: boolean;
  dropdownStyle?: StyleProp<ViewStyle>;
};

export function ClosingTimeForm({
  schoolClassOptions,
  initialValues,
  onSubmit: onSubmitProp,
  onScopeOrClassIdDirtyChange,
  submitButtonText,
  actionsNotice,
  isSubmitDisabled: isSubmitDisabledProp,
  renderActions,
  noteComponent,
  isDateRangeDisabled = false,
  isScopeDisabled = false,
  isEditorDisabled = false,
  isClosingNoticeDisabled = false,
  fieldLabelMinHeight,
  reserveErrorSpace = true,
  dropdownStyle,
}: ClosingTimeFormProps) {
  const { t } = useTranslation();
  const { colors, fonts } = useCustomTheme();

  const closingTimeSchema = useMemo(() => createClosingTimeFormSchema(t), [t]);

  const getMergedDefaultValues = useCallback(
    (currentValues?: Partial<ClosingTimeFormValues>) => ({
      editor: initialValues?.editor ?? currentValues?.editor ?? "",
      closingNotice:
        initialValues?.closingNotice ?? currentValues?.closingNotice ?? "",
      dateFrom: initialValues?.dateFrom ?? currentValues?.dateFrom ?? "",
      dateTo: initialValues?.dateTo ?? currentValues?.dateTo ?? "",
      scopeOrClassId:
        initialValues?.scopeOrClassId ??
        currentValues?.scopeOrClassId ??
        undefined,
    }),
    [initialValues],
  );

  const formDefaultValues = useMemo(
    () => getMergedDefaultValues(),
    [getMergedDefaultValues],
  );

  const form = useFormWrapper<typeof closingTimeSchema>({
    schema: closingTimeSchema,
    defaultValues: formDefaultValues,
    mode: "onSubmit",
    rethrowSubmitError: true,
  });

  const [editor, closingNotice, dateFrom, dateTo, scopeOrClassId] = useWatch({
    control: form.control,
    name: ["editor", "closingNotice", "dateFrom", "dateTo", "scopeOrClassId"],
  });
  const { resetForm } = form;

  const watchedValues = useMemo(
    () =>
      ({
        editor: editor ?? "",
        closingNotice: closingNotice ?? "",
        dateFrom: dateFrom ?? "",
        dateTo: dateTo ?? "",
        scopeOrClassId: scopeOrClassId ?? "",
      }) satisfies ClosingTimeFormValues,
    [closingNotice, dateFrom, dateTo, editor, scopeOrClassId],
  );

  const isSubmitDisabled = Boolean(isSubmitDisabledProp?.(watchedValues));
  const fieldLabelStyle = useMemo(
    () =>
      fieldLabelMinHeight
        ? [styles.fieldLabel, { minHeight: fieldLabelMinHeight }]
        : styles.fieldLabel,
    [fieldLabelMinHeight],
  );

  useEffect(() => {
    resetForm(formDefaultValues);
  }, [formDefaultValues, resetForm]);

  const scopeOrClassIdIsDirty = Boolean(
    form.formState.dirtyFields.scopeOrClassId,
  );

  useEffect(() => {
    onScopeOrClassIdDirtyChange?.(scopeOrClassIdIsDirty);
  }, [onScopeOrClassIdDirtyChange, scopeOrClassIdIsDirty]);

  const onSubmit: Parameters<typeof form.submit>[0] = async ({ values }) => {
    if (onSubmitProp) {
      await onSubmitProp(values);
    }
  };

  const actionsNode = renderActions ? (
    renderActions({
      submit: () => {
        void form.submit(onSubmit).catch(() => undefined);
      },
      reset: () => resetForm(formDefaultValues),
      isSubmitting: form.formState.isSubmitting,
      isSubmitDisabled,
    })
  ) : (
    <View style={styles.buttonContainer}>
      <ResetButton
        title={t("closeTimes.form.resetButtonText")}
        buttonStyle={[styles.resetButton, { borderColor: colors.text }]}
        buttonTextStyle={[
          styles.buttonText,
          { color: colors.text, ...fonts.bold },
        ]}
        iconName="delete-outline"
        iconPosition="left"
        iconColor={colors.text}
      />
      <SubmitButton
        title={submitButtonText ?? t("closeTimes.form.submitButtonText")}
        disabled={form.formState.isSubmitting || isSubmitDisabled}
        onPress={() => {
          void form.submit(onSubmit).catch(() => undefined);
        }}
        buttonStyle={[styles.submitButton, { backgroundColor: colors.border }]}
        buttonTextStyle={[
          styles.buttonText,
          { color: colors.background, ...fonts.bold },
        ]}
        iconName="arrow-right"
        iconPosition="right"
        iconColor={colors.background}
      />
    </View>
  );

  return (
    <FormProvider form={form}>
      <View style={styles.wrapper}>
        <View style={styles.formContainer}>
          <View style={styles.formElement}>
            <ThemedText style={fieldLabelStyle}>
              {t("closeTimes.form.dateLabel")}*
            </ThemedText>
            <View style={styles.formContainer}>
              <View style={styles.formElement}>
                <FormDatePicker
                  name="dateFrom"
                  placeholder={t("closeTimes.form.dateFromPlaceholder")}
                  disabled={isDateRangeDisabled}
                  minDate={getEarliestClosingTimeRequestStartDate()}
                  maxDate={dateTo ? parseISO(dateTo) : undefined}
                />
              </View>
              <View style={styles.formElement}>
                <FormDatePicker
                  name="dateTo"
                  placeholder={t("closeTimes.form.dateToPlaceholder")}
                  disabled={isDateRangeDisabled}
                  minDate={
                    dateFrom
                      ? parseISO(dateFrom)
                      : getEarliestClosingTimeRequestStartDate()
                  }
                  initialMonth={dateFrom ? parseISO(dateFrom) : undefined}
                />
              </View>
            </View>
          </View>
          <View style={styles.formElement}>
            <ThemedText style={fieldLabelStyle}>
              {t("closeTimes.form.scopeLabel")}*
            </ThemedText>
            <View style={styles.formContainer}>
              <View style={[styles.formElement, dropdownStyle]}>
                <FormSelect
                  icon={{
                    iconSet: "material-community",
                    icon: "school-outline",
                  }}
                  name="scopeOrClassId"
                  options={schoolClassOptions}
                  disabled={isScopeDisabled}
                  clearable={false}
                  placeholder={t("closeTimes.form.scopePlaceholder")}
                />
              </View>
            </View>
          </View>
        </View>
        <View style={styles.formContainer}>
          <View style={styles.formElement}>
            <ThemedText style={fieldLabelStyle}>
              {t("closeTimes.form.editorLabel")}*
            </ThemedText>
            <FormTextInput
              name="editor"
              icon={{
                iconSet: "material-community",
                icon: "account-circle-outline",
              }}
              disabled={isEditorDisabled}
              label={t("closeTimes.form.editorPlaceholder")}
            />
          </View>
          <View style={styles.formElement}>
            <ThemedText style={fieldLabelStyle}>
              {t("closeTimes.form.closingNoticeLabel")}*
            </ThemedText>
            <FormTextInput
              name="closingNotice"
              disabled={isClosingNoticeDisabled}
              label={t("closeTimes.form.closingNoticePlaceholder")}
            />
          </View>
        </View>
        {noteComponent}
        <FormError reserveSpace={reserveErrorSpace} />
        {actionsNotice}
        {actionsNode}
      </View>
    </FormProvider>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    gap: 16,
  },
  submitButton: {
    borderRadius: 56,
    height: 56,
    paddingHorizontal: 16,
    gap: 0,
  },
  resetButton: {
    paddingHorizontal: 16,
    borderRadius: 56,
    borderWidth: 2,
    height: 56,
    gap: 0,
  },
  buttonText: {
    fontSize: 16,
    paddingHorizontal: 8,
  },
  formContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    gap: 32,
  },
  formElement: {
    flex: 1,
    gap: 16,
  },
  fieldLabel: {
    lineHeight: 20.8,
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
});
